import {
  Outlet,
  createFileRoute,
  notFound,
  useParams,
  useRouterState,
} from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { DashboardHeader } from '@/components/dashboard/header/dashboard-header'
import { DashboardNotFound } from '@/components/dashboard/not-found'
import { hasAccessToken } from '@/lib/token-storage'
import { redirectToLoginApp } from '@/lib/login-app-url'
import { useFunis } from '@/hooks/use-funis'
import { usePortais } from '@/hooks/use-portais'
import { useConversationsWebSocket } from '@/hooks/use-conversations-websocket'
import { PushNotificationsBootstrap } from '@/components/pwa/push-notifications-bootstrap'
import { GlobalMessageNotifications } from '@/components/conversas/global-message-notifications'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { DashboardSidebar } from '@/components/dashboard/dashboard-sidebar'
import { useUnreadStats } from '@/hooks/use-unread-stats'
import { useUserProfile } from '@/hooks/use-user-profile'
import { useTarefas } from '@/hooks/use-tarefas'
import { useNovosLeadsCount } from '@/hooks/use-novos-leads-count'
import { usePlanModules } from '@/hooks/use-plan-modules'
import { NoActivePlanDialog } from '@/components/dashboard/no-active-plan-dialog'
import { BlockedAccountDialog } from '@/components/dashboard/blocked-account-dialog'
import { useDashboard } from '@/hooks/use-dashboard'
import { TrialWarningBar } from '@/components/dashboard/trial-warning-bar'
import { CheckinRenovacaoAlert } from '@/components/dashboard/checkin-renovacao-alert'
import {
  useUpdateUserPreferences,
  useUserPreferences,
} from '@/hooks/use-user-preferences'
import { IsaCopilot } from '@/components/isa-copilot'
import { IsaCopilotChatProvider } from '@/components/isa-copilot/isa-copilot-chat-provider'
import { IsaCopilotUIProvider } from '@/components/isa-copilot/isa-copilot-ui-provider'
import { isDashboardBlockedByBilling } from '@/lib/dashboard-access'
import { isIsaChatPagePath } from '@/lib/isa-chat-routes'
import { cn } from '@/lib/utils'

// Layout Route normal - path é /dashboard
// As rotas filhas (dashboard.$dashboardId.tsx) serão automaticamente envolvidas
// O path deve corresponder ao caminho do arquivo na estrutura de rotas
// Nota: A pasta (dashboard) é apenas organizacional e não afeta o path da rota
export const Route = createFileRoute('/(dashboard)/dashboard')({
  beforeLoad: ({ location }) => {
    // Verifica autenticação primeiro
    if (!hasAccessToken()) {
      throw redirectToLoginApp()
    }

    // Se acessar /dashboard/ diretamente (sem ID), lançar notFound
    const pathname = location.pathname.replace(/\/$/, '') // Remove barra final

    // Verifica se é exatamente /dashboard (sem segmentos adicionais)
    // A rota /dashboard/$dashboardId terá pathname como /dashboard/1, /dashboard/123, etc
    if (pathname === '/dashboard') {
      // Lança notFound direcionando para esta própria rota que tem o notFoundComponent
      throw notFound({ routeId: Route.id })
    }
  },
  component: DashboardLayout,
  notFoundComponent: DashboardNotFound,
})

// Layout Route normal - aparece na URL como /dashboard
// Envolve todas as rotas filhas (como dashboard.$dashboardId.tsx)
function DashboardLayout() {
  // Pega o dashboardId dos params da URL (da rota filha)
  const params = useParams({ strict: false })
  const dashboardId = (params as any)?.dashboardId || ''
  const router = useRouterState()
  const pathname = router.location.pathname

  // Manter conexão WebSocket ativa em todas as rotas do dashboard
  // Isso garante que notificações sejam recebidas mesmo fora da página de conversas
  // O DashboardHeader também tem seus próprios callbacks para atualizar o contador
  useConversationsWebSocket(dashboardId || null, {
    onNewMessage: () => {
      // Notificações de novas mensagens serão tratadas pelos componentes específicos
      // O DashboardHeader tem seu próprio callback para atualizar estatísticas
    },
    onNewConversation: () => {
      // Notificações de novas conversas serão tratadas pelos componentes específicos
    },
    onConversationListUpdated: () => {
      // Atualizações da lista de conversas serão tratadas pelo GlobalMessageNotifications
      // O DashboardHeader também escuta este evento para atualizar estatísticas
    },
    onUnreadStatsUpdated: () => {
      // Atualizações de estatísticas de não lidas serão tratadas pelos componentes específicos
      // O DashboardHeader tem seu próprio callback para atualizar estatísticas
    },
  })

  // Busca os funis da API
  const { data: funisData } = useFunis(dashboardId)

  // Busca os portais da API
  const { data: portaisData } = usePortais(dashboardId)
  
  // Busca informações do dashboard e plano
  const { data: dashboardData, isLoading: isLoadingDashboard } = useDashboard(dashboardId)
  const { hasActivePlan, isLoading: isLoadingPlan } = usePlanModules(dashboardId)

  // Não exibe dialogs bloqueantes na página de assinatura
  const isAssinarPage = pathname.includes('/assinar')
  const isChatPage = isIsaChatPagePath(pathname, dashboardId)
  const isFillHeightPage =
    isChatPage ||
    pathname.replace(/\/$/, '').endsWith('/sistema/whatsapp/nova')

  // Status do dashboard (Ativo, Trial, Cancelado, Suspenso, Pendente)
  const dashboardStatus = dashboardData?.data?.status
  const trustUnlockUntil = dashboardData?.data?.desbloqueioConfiancaAte
  const isBlockedStatus = isDashboardBlockedByBilling({
    status: dashboardStatus,
    trustUnlockUntil,
  })

  // Aguarda ambos carregarem antes de decidir qual dialog mostrar
  // Isso evita que o "Plano Inativo" apareça brevemente antes do status do dashboard ser conhecido
  const isLoadingAny = isLoadingPlan || isLoadingDashboard

  // Dialog de plano inativo só aparece quando:
  // - tudo carregou
  // - não tem plano ativo
  // - conta NÃO está cancelada/suspensa (que tem dialog próprio)
  // - usuário NÃO está na página de assinatura
  const showNoPlanDialog = !isLoadingAny && !hasActivePlan && !isBlockedStatus && !isAssinarPage

  // Extrai o funilValue da URL se estiver na rota de oportunidades
  // Exemplo: /dashboard/123/oportunidades/vendas -> funilValue = "vendas"
  const funilValueMatch = pathname.match(
    /\/dashboard\/\d+\/oportunidades\/([^/]+)/,
  )
  const funilValue = funilValueMatch ? funilValueMatch[1] : undefined

  // Transforma os dados da API para o formato esperado pelo header
  const funis = funisData?.data || []
  const portais = portaisData?.data || []

  // Busca estatísticas de mensagens não lidas
  const { data: unreadStats } = useUnreadStats(dashboardId)
  const unreadMessagesCount = unreadStats?.totalUnreadMessages || 0
  const { data: novosLeadsData } = useNovosLeadsCount(dashboardId)
  const novosLeadsCount = novosLeadsData?.data?.count || 0

  // Busca perfil do usuário para obter o ID
  const { data: profileData } = useUserProfile()
  const userId = profileData?.data?.id

  const { data: preferencesData, isLoading: isLoadingPreferences } =
    useUserPreferences(dashboardId)
  const { mutate: persistPreferences } = useUpdateUserPreferences(dashboardId)

  const preferencesPatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  useEffect(() => {
    return () => {
      if (preferencesPatchTimerRef.current) {
        clearTimeout(preferencesPatchTimerRef.current)
      }
    }
  }, [])

  const schedulePreferencesPatch = useCallback(
    (patch: Record<string, any>) => {
      if (!dashboardId) return

      if (preferencesPatchTimerRef.current) {
        clearTimeout(preferencesPatchTimerRef.current)
      }

      preferencesPatchTimerRef.current = setTimeout(() => {
        persistPreferences({
          dashboardId: Number(dashboardId),
          preferences: patch,
        })
      }, 400)
    },
    [dashboardId, persistPreferences],
  )

  // Busca tarefas do usuário atual
  const { data: tarefasData } = useTarefas({
    dashboardId,
    enabled: !!userId,
    responsaveis: userId ? [userId] : [],
    orderBy: 'agendadoPara',
    order: 'asc',
  })

  // Conta tarefas atrasadas do usuário atual
  const overdueTasksCount = useMemo(() => {
    if (!tarefasData?.pages?.[0]?.data || !Array.isArray(tarefasData.pages[0].data)) {
      return 0
    }

    const agora = new Date()
    const tarefas = tarefasData.pages[0].data.filter(
      (tarefa: any) =>
        tarefa.statusValue !== 'concluida' &&
        tarefa.statusValue !== 'cancelada' &&
        tarefa.agendadoPara,
    )

    const atrasadas = tarefas.filter((tarefa: any) => {
      if (!tarefa.agendadoPara) return false
      const dataAgendada = new Date(tarefa.agendadoPara)
      return dataAgendada < agora
    })

    return atrasadas.length
  }, [tarefasData])

  const uiPreferences = preferencesData?.data?.preferences?.ui || {}
  const sidebarDefaultOpen =
    typeof uiPreferences.sidebarOpen === 'boolean'
      ? uiPreferences.sidebarOpen
      : true
  const sidebarDefaultWidth =
    typeof uiPreferences.sidebarWidth === 'number'
      ? uiPreferences.sidebarWidth
      : 256

  if (dashboardId && isLoadingPreferences) {
    return null
  }

  return (
    <SidebarProvider
      className="h-svh overflow-hidden"
      defaultOpen={sidebarDefaultOpen}
      defaultWidth={sidebarDefaultWidth}
      onPersistOpenChange={(open) => {
        schedulePreferencesPatch({
          ui: {
            sidebarOpen: open,
          },
        })
      }}
      onPersistWidthChange={(width) => {
        schedulePreferencesPatch({
          ui: {
            sidebarWidth: width,
          },
        })
      }}
    >
      {dashboardId && (
        <>
          {/* Dialog bloqueante quando não há plano ativo */}
          <NoActivePlanDialog 
            open={showNoPlanDialog} 
            dashboardName={dashboardData?.data?.titulo}
            dashboardId={dashboardId}
          />

          {/* Dialog bloqueante para conta Cancelada ou Suspensa (oculto na página /assinar) */}
          {isBlockedStatus && !isAssinarPage && (dashboardStatus === 'Cancelado' || dashboardStatus === 'Suspenso') && (
            <BlockedAccountDialog
              open={true}
              status={dashboardStatus}
              dashboardName={dashboardData?.data?.titulo}
              dashboardId={dashboardId}
            />
          )}
          
          <DashboardSidebar
            dashboardId={dashboardId}
            funis={funis}
            novosLeads={novosLeadsCount}
            unreadMessagesCount={unreadMessagesCount}
            overdueTasksCount={overdueTasksCount}
          />
          <SidebarInset className="overflow-x-hidden max-w-full min-h-0 flex flex-row flex-1">
            <IsaCopilotChatProvider dashboardId={dashboardId}>
              <IsaCopilotUIProvider dashboardId={dashboardId}>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden">
                  <DashboardHeader
                    dashboardId={dashboardId}
                    novosLeads={novosLeadsCount}
                    funilValue={funilValue}
                    funis={funis}
                    portais={portais}
                  />
                  <TrialWarningBar dashboardId={dashboardId} />
                  {/* Componente para gerenciar notificações globais de mensagens */}
                  <PushNotificationsBootstrap />
                  <GlobalMessageNotifications dashboardId={dashboardId} />
                  {/* Alerta de renovação de check-in de plantão */}
                  <CheckinRenovacaoAlert dashboardId={dashboardId} />
                  <div
                    className={cn(
                      'flex min-h-0 w-full flex-1 flex-col overflow-x-hidden max-w-full',
                      isFillHeightPage ? 'overflow-hidden' : 'overflow-y-auto',
                    )}
                  >
                    {/* O <Outlet /> renderiza as rotas filhas */}
                    <Outlet />
                  </div>
                </div>
                <IsaCopilot dashboardId={dashboardId} />
              </IsaCopilotUIProvider>
            </IsaCopilotChatProvider>
          </SidebarInset>
        </>
      )}
    </SidebarProvider>
  )
}
